<template>
  <div class="page-container fault-page">
    <!-- 顶部统计卡片 -->
    <div class="stats-row">
      <div class="stat-item danger">
        <div class="stat-item-value">{{ stats.urgentCount }}</div>
        <div class="stat-item-label">紧急故障</div>
      </div>
      <div class="stat-item warning">
        <div class="stat-item-value">{{ stats.normalCount }}</div>
        <div class="stat-item-label">一般故障</div>
      </div>
      <div class="stat-item success">
        <div class="stat-item-value">{{ stats.monthFixedCount }}</div>
        <div class="stat-item-label">本月已处理</div>
      </div>
    </div>

    <div class="card">
      <div class="tabs-header-wrapper">
        <el-tabs v-model="activeTab" class="main-tabs" @tab-click="handleTabClick">
          <el-tab-pane label="待处理" name="unfinished" />
          <el-tab-pane label="已处理" name="finished" />
          <el-tab-pane label="全部" name="all" />
        </el-tabs>

        <div class="action-btn-group">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增故障单</el-button>
          <el-button v-if="activeTab === 'unfinished'" type="warning" size="small" icon="el-icon-refresh" @click="handleBatchReassign">批量改派</el-button>
          <el-button size="small" icon="el-icon-download" :loading="exportLoading" :disabled="!tableData.length" @click="handleExport">台账导出</el-button>
        </div>
      </div>

      <div class="search-bar">
        <el-input v-model="queryForm.keyword" placeholder="故障编号/设备名称" clearable style="width: 180px" @keyup.enter.native="handleQuery" />

        <AreaCascader v-model="queryForm.areaId" :options="areaTree" placeholder="所属区域" clearable style="width: 180px" @change="handleQuery" />

        <el-select v-model="queryForm.status" placeholder="处理状态" clearable style="width: 130px" :disabled="activeTab === 'finished'" @change="handleQuery">
          <el-option v-for="opt in statusSelectOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>

        <el-select v-model="queryForm.faultLevel" placeholder="故障级别" clearable style="width: 120px" @change="handleQuery">
          <el-option label="紧急" value="urgent" />
          <el-option label="一般" value="normal" />
        </el-select>
        <el-select v-model="queryForm.faultSource" placeholder="录入来源" clearable style="width: 120px" @change="handleQuery">
          <el-option label="巡查发现" value="task" />
          <el-option label="人工录入" value="manual" />
          <el-option label="日常任务" value="daily_task" />
          <el-option label="机器人上报" value="robot" />
          <el-option label="云平台导入" value="cloud" />
        </el-select>
        <el-select v-if="activeTab === 'unfinished'" v-model="queryForm.assignStatus" placeholder="是否已分配" clearable style="width: 130px" @change="handleQuery">
          <el-option label="已分配" value="assigned" />
          <el-option label="未分配" value="unassigned" />
        </el-select>
        <el-date-picker
          v-if="activeTab !== 'finished'"
          v-model="foundDateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="发现时间起"
          end-placeholder="发现时间止"
          value-format="yyyy-MM-dd HH:mm:ss"
          style="width: 260px"
          @change="handleFoundDateChange"
        />
        <el-date-picker
          v-if="activeTab === 'finished'"
          v-model="handleDateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="处理时间起"
          end-placeholder="处理时间止"
          value-format="yyyy-MM-dd HH:mm:ss"
          style="width: 260px"
          @change="handleHandleDateChange"
        />

        <el-button type="primary" size="small" icon="el-icon-search" @click="handleQuery">搜索</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 故障台账表格 -->
      <div class="table-wrapper">
        <el-table
          :key="activeTab"
          v-loading="loading"
          row-key="uuid"
          :data="tableData"
          border
          stripe
          style="width: 100%"
          height="100%"
          :header-cell-style="{ textAlign: 'center' }"
          :cell-style="{ textAlign: 'center' }"
        >
          <el-table-column prop="faultCode" label="故障编号" width="130">
            <template slot-scope="{ row }">
              <span class="table-code-link" @click.stop="handleDetail(row)">
                {{ row.faultCode }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="设备信息" min-width="180">
            <template slot-scope="{ row }">
              <div class="device-info">
                <span>{{ row.deviceName }}</span>
                <el-tag
                  v-if="row.deviceSource === 'external'"
                  size="mini"
                  style="margin-left: 6px"
                >
                  非系统设备
                </el-tag>
                <!-- A级重要设备标红 -->
                <el-tag
                  v-if="row.deviceLevel === 'A'"
                  type="danger"
                  size="mini"
                  style="margin-left: 6px"
                >
                  重要设备
                </el-tag>
              </div>
              <div class="device-area">{{ row.areaPath }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="faultDesc" label="故障描述" min-width="180" show-overflow-tooltip :formatter="formatFaultDescCell" />
          <el-table-column label="故障图片" width="110">
            <template slot-scope="{ row }">
              <!-- 表格内只渲染首图（角标提示总数），点击弹窗查看全部，避免多图撑高行 -->
              <ImageThumbViewer
                v-if="hasImages(row.faultImages)"
                :file-ids="row.faultImages"
                variant="compact"
                :max-visible="1"
                preview-all
                class="fault-image-cell"
              />
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column label="故障级别" width="70">
            <template slot-scope="{ row }">
              <el-tag :type="getFaultLevelType(row.faultLevel)" size="small">
                {{ row.faultLevelName || row.faultLevel }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="处理状态" width="100">
            <template slot-scope="{ row }">
              <el-tag :type="getStatusType(row.status)" size="small">
                {{ row.statusName || row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="录入来源" width="90">
            <template slot-scope="{ row }">
              {{ row.faultSourceName || row.faultSource }}
            </template>
          </el-table-column>
          <!-- 已处理：处理时间；待处理/全部：发现时间 -->
          <el-table-column :label="activeTab === 'finished' ? '处理时间' : '发现时间'" width="140">
            <template slot-scope="{ row }">
              {{ formatDateTime(activeTab === 'finished' ? (row.handleTime || row.fixTime) : row.foundTime) }}
            </template>
          </el-table-column>
          <!-- 已处理：处置人员、处置描述、处置图片 -->
          <el-table-column v-if="activeTab === 'finished'" label="处置人员" min-width="100">
            <template slot-scope="{ row }">
              <span v-if="row.handleUserName">{{ row.handleUserName }}</span>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column v-if="activeTab === 'finished'" prop="handleDesc" label="处置描述" min-width="180" show-overflow-tooltip :formatter="formatTextCell" />
          <el-table-column v-if="activeTab === 'finished'" label="处置图片" width="110">
            <template slot-scope="{ row }">
              <ImageThumbViewer
                v-if="hasImages(row.handleImages)"
                :file-ids="row.handleImages"
                variant="compact"
                :max-visible="1"
                preview-all
                class="fault-image-cell"
              />
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <!-- 待处理：分配信息 -->
          <el-table-column v-if="activeTab === 'unfinished'" label="分配信息" show-overflow-tooltip min-width="120">
            <template slot-scope="{ row }">
              <span v-if="!row.assignType || row.assignType === 'none'" class="text-muted">
                未指定
              </span>
              <template v-else-if="row.assignType === 'team'">
                <el-tag type="warning" size="mini" effect="plain">
                  {{ row.assignTeamName || '指定小组' }}
                </el-tag>
                <div v-if="row.teamAssigneeName" class="assign-delegate-text">
                  组内委派：{{ row.teamAssigneeName }}
                </div>
              </template>
              <span v-else>{{ row.assignUserNames || row.assignUserIds || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="primary" plain size="mini" @click="handleDetail(row)">查看详情</el-button>
              <!-- 已维修不可改派，其余状态均可 -->
              <el-button v-if="row.status !== 'fixed'" type="info" plain size="mini" @click="handleReassign(row)">改派</el-button>
              <!-- 已维修状态不可处置 -->
              <el-button v-if="row.status !== 'fixed'" type="warning" plain size="mini" @click="handleOperate(row)">处置</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <el-pagination
        class="pagination"
        :current-page="queryForm.pageNo"
        :page-sizes="[10, 20, 50]"
        :page-size="queryForm.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>

    <!-- 新增故障单弹窗 -->
    <FaultCreateDialog v-if="createDialogVisible" :visible.sync="createDialogVisible" @success="handleCreateSuccess" />

    <!-- 批量改派弹窗，仅在待处理页入口展示 -->
    <FaultBatchReassignDialog v-if="batchReassignVisible" :visible.sync="batchReassignVisible" @success="handleBatchReassignSuccess" />

    <!-- 故障处置弹窗 -->
    <FaultHandleDialog v-if="handleDialogVisible" :visible.sync="handleDialogVisible" :fault="currentFault" @success="handleOperateSuccess" />

    <!-- 故障详情弹窗 -->
    <FaultDetailDialog v-if="detailDialogVisible" :visible.sync="detailDialogVisible" :fault-id="currentFaultId" />

    <!-- 故障导出弹窗 -->
    <FaultExportDialog
      v-if="exportDialogVisible"
      :visible.sync="exportDialogVisible"
      :loading="exportLoading"
      @confirm-export="confirmExport"
    />

    <!-- 改派弹窗 -->
    <el-dialog
      title="改派故障单"
      :visible.sync="reassignVisible"
      width="680px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form
        :model="reassignForm"
        label-width="90px"
      >
        <div
          v-if="reassignFault"
          class="task-info-bar"
        >
          <span class="label">故障单：</span>
          <span class="value">{{ reassignFault.faultCode }}</span>
          <span
            class="label"
            style="margin-left:16px"
          >提示：</span>
          <span
            class="value"
            style="color:#e6a23c"
          >改派后原小组委派记录将清空</span>
        </div>
        <!-- 分配方式 + 指定个人/小组 -->
        <AssignTypeFields
          :model="reassignForm"
          team-type="fault"
          preset="fault"
          label-width="90px"
          :active="reassignVisible"
          @navigate-to-team="handleReassignNavigateTeam"
          @navigate-to-org-user="handleReassignNavigateOrgUser"
        />
      </el-form>
      <div slot="footer">
        <el-button @click="reassignVisible = false">
          取消
        </el-button>
        <el-button
          type="primary"
          :loading="reassigning"
          @click="submitReassign"
        >
          确认改派
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
// 服务
import { faultService } from './fault.service';
import { areaService } from '@/features/gnmk/area/area.service';
// 工具函数
import { downloadFromApiBlobResponse } from '@/features/common/utils/downloadFileUtil.js';
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
import { parseImageFileIds } from '@/features/components/common-image/imageFileIdsUtil.js';
import { formatFaultDesc } from '@/features/gnmk/fault/utils/faultHandleUtils.js';
// 组件
import FaultCreateDialog from './faultCreateDialog.vue';
import FaultBatchReassignDialog from './faultBatchReassignDialog.vue';
import FaultHandleDialog from './faultHandleDialog.vue';
import FaultDetailDialog from './faultDetailDialog.vue';
import FaultExportDialog from './faultExportDialog.vue';
import AssignTypeFields from '@/features/gnmk/components/AssignTypeFields.vue';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';
import ImageThumbViewer from '@/features/components/common-image/ImageThumbViewer.vue';

export default {
  name: 'FaultPage',
  components: { FaultCreateDialog, FaultBatchReassignDialog, FaultHandleDialog, FaultDetailDialog, FaultExportDialog, AssignTypeFields, AreaCascader, ImageThumbViewer },
  data () {
    return {
      loading: false,
      tableData: [],
      total: 0,
      // 顶部统计
      stats: { urgentCount: 0, normalCount: 0, monthFixedCount: 0 },
      // 查询表单
      queryForm: {
        keyword: '',
        areaId: '',
        status: '',
        faultLevel: '',
        faultSource: '',
        assignStatus: '',
        foundTimeStart: null,
        foundTimeEnd: null,
        handleTimeStart: null,
        handleTimeEnd: null,
        pageNo: 1,
        pageSize: 10
      },
      areaTree: [],
      foundDateRange: null,
      handleDateRange: null,
      activeTab: 'unfinished',
      exportLoading: false,
      exportDialogVisible: false,
      // 弹窗控制
      createDialogVisible: false,
      batchReassignVisible: false,
      handleDialogVisible: false,
      detailDialogVisible: false,
      currentFault: null,
      currentFaultId: null,
      // 改派弹窗相关
      reassignVisible: false,
      reassignFault: null,
      reassignForm: { assignType: 'none', assignUserIds: [], assignTeamId: null },
      reassigning: false,
      /** keep-alive 下首次 activated 与 created 连续触发，避免重复请求 */
      _faultListActivatedOnce: false
    };
  },
  computed: {
    // 动态调整状态下拉选项
    statusSelectOptions () {
      const all = [
        { label: '待处理', value: 'pending' },
        { label: '已上报待处理', value: 'reported' },
        { label: '已维修', value: 'fixed' }
      ];
      if (this.activeTab === 'unfinished') {
        return all.filter(o => o.value !== 'fixed');
      } else if (this.activeTab === 'finished') {
        return all.filter(o => o.value === 'fixed');
      }
      return all;
    }
  },
  methods: {
    updateStatistics () {
      faultService.getFaultStatistics().then(res => {
        if (res.data) this.stats = res.data;
      }).catch(() => {});
    },

    loadBaseData () {
      areaService.getAreaTree().then(res => (this.areaTree = res.data || []));
    },

    /** 组装分页/导出查询参数：待处理 Tab 未选具体状态时查 pending+reported */
    buildFaultListQueryParams () {
      const { status, ...rest } = this.queryForm;
      const params = { ...rest };
      if (this.activeTab === 'unfinished') {
        if (status) {
          params.status = status;
        } else {
          params.statusList = ['pending', 'reported'];
        }
      } else if (this.activeTab === 'finished') {
        params.status = 'fixed';
      } else if (status) {
        params.status = status;
      }
      return params;
    },

    loadData () {
      this.loading = true;
      faultService.queryFaultPage(this.buildFaultListQueryParams()).then(res => {
        const data = res.data;
        this.tableData = data.list || data.rows || [];
        this.total = data.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },

    handleQuery () {
      this.queryForm.pageNo = 1;
      this.loadData();
      this.updateStatistics();
    },

    handleReset () {
      this.queryForm = {
        keyword: '',
        areaId: '',
        status: '',
        faultLevel: '',
        faultSource: '',
        assignStatus: '',
        foundTimeStart: null,
        foundTimeEnd: null,
        handleTimeStart: null,
        handleTimeEnd: null,
        pageNo: 1,
        pageSize: 10
      };
      this.foundDateRange = null;
      this.handleDateRange = null;
      // 保持当前的 Tab 状态
      if (this.activeTab === 'finished') {
        this.queryForm.status = 'fixed';
      }
      this.loadData();
      this.updateStatistics();
    },

    // 处理 Tab 切换的逻辑
    handleTabClick () {
      // 切换 Tab 时重置页码
      this.queryForm.pageNo = 1;
      // 切换时清空状态，避免选项冲突
      this.queryForm.status = '';
      // 分配状态仅适用于待处理列表，切换到其他 Tab 时避免隐藏条件继续生效
      if (this.activeTab !== 'unfinished') {
        this.queryForm.assignStatus = '';
      }

      // 已处理 Tab 固定只查 fixed
      if (this.activeTab === 'finished') {
        this.queryForm.status = 'fixed';
      }

      // 与可见筛选项一致：隐藏的发现/处理时间条件不参与查询
      if (this.activeTab === 'finished') {
        this.queryForm.foundTimeStart = null;
        this.queryForm.foundTimeEnd = null;
        this.foundDateRange = null;
      } else {
        this.queryForm.handleTimeStart = null;
        this.queryForm.handleTimeEnd = null;
        this.handleDateRange = null;
      }

      this.loadData();
      this.updateStatistics();
    },

    handleFoundDateChange (val) {
      if (val) {
        this.queryForm.foundTimeStart = val[0];
        this.queryForm.foundTimeEnd = val[1];
      } else {
        this.queryForm.foundTimeStart = null;
        this.queryForm.foundTimeEnd = null;
      }
      this.handleQuery();
    },

    handleHandleDateChange (val) {
      if (val) {
        this.queryForm.handleTimeStart = val[0];
        this.queryForm.handleTimeEnd = val[1];
      } else {
        this.queryForm.handleTimeStart = null;
        this.queryForm.handleTimeEnd = null;
      }
      this.handleQuery();
    },

    handleSizeChange (size) {
      this.queryForm.pageSize = size;
      this.loadData();
    },

    handlePageChange (page) {
      this.queryForm.pageNo = page;
      this.loadData();
    },

    handleAdd () {
      this.createDialogVisible = true;
    },

    handleBatchReassign () {
      if (this.activeTab !== 'unfinished') return;
      this.batchReassignVisible = true;
    },

    handleDetail (row) {
      this.currentFaultId = row.uuid;
      this.detailDialogVisible = true;
    },

    handleOperate (row) {
      this.currentFault = row;
      this.handleDialogVisible = true;
    },

    hasImages (fileIds) {
      return parseImageFileIds(fileIds).length > 0;
    },

    /** 列表单元格不放 tooltip 自带的原始换行，统一走故障模块的展示规则 */
    formatFaultDescCell (row) {
      return formatFaultDesc(row.faultDesc);
    },

    /** 普通描述类单元格：空值补 -，配合 show-overflow-tooltip 省略长文本 */
    formatTextCell (row, column, cellValue) {
      return cellValue || '-';
    },

    handleExport () {
      this.exportDialogVisible = true;
    },

    confirmExport (options) {
      this.exportDialogVisible = false;
      this.exportLoading = true;
      faultService.exportFault({ ...this.buildFaultListQueryParams(), ...options }).then(res => {
        downloadFromApiBlobResponse(res, '故障列表.xlsx');
        this.$message.success('导出成功');
      }).finally(() => {
        this.exportLoading = false;
      });
    },

    handleCreateSuccess () {
      this.createDialogVisible = false;
      this.loadData();
      this.updateStatistics();
    },

    handleBatchReassignSuccess () {
      this.batchReassignVisible = false;
      this.loadData();
      this.updateStatistics();
    },

    handleOperateSuccess () {
      this.handleDialogVisible = false;
      this.loadData();
      this.updateStatistics();
    },

    // ========== 字典映射 ==========

    getFaultLevelType (level) {
      return { urgent: 'danger', normal: 'warning', resolved: 'success' }[level] || '';
    },

    getStatusType (status) {
      return { pending: 'warning', reported: 'primary', fixed: 'success' }[status] || '';
    },

    formatDateTime,

    // ========== 改派相关方法 ==========

    handleReassign (row) {
      this.reassignFault = row;
      this.reassignForm = {
        assignType: row.assignType || 'none',
        assignUserIds: row.assignUserIds ? row.assignUserIds.split(',').filter(Boolean) : [],
        assignTeamId: row.assignTeamId || null
      };
      this.reassignVisible = true;
    },

    handleReassignNavigateTeam () {
      this.reassignVisible = false;
      this.$router.push({ name: 'teamList' }).catch(() => {});
    },
    handleReassignNavigateOrgUser () {
      this.reassignVisible = false;
      this.$router.push({ name: 'orgUserList' }).catch(() => {});
    },

    submitReassign () {
      const form = this.reassignForm;
      if (form.assignType === 'user' && !form.assignUserIds.length) {
        this.$message.warning('请选择至少一名执行人员');
        return;
      }
      if (form.assignType === 'team' && !form.assignTeamId) {
        this.$message.warning('请选择执行小组');
        return;
      }
      this.reassigning = true;
      faultService.reassignFault({
        faultId: this.reassignFault.uuid,
        assignType: form.assignType,
        assignUserIds: form.assignType === 'user' ? form.assignUserIds.join(',') : '',
        assignTeamId: form.assignType === 'team' ? form.assignTeamId : ''
      }).then(() => {
        this.$message.success('改派成功');
        this.reassignVisible = false;
        this.loadData();
      }).finally(() => {
        this.reassigning = false;
      });
    },

    /**
     * 从路由 query 恢复列表 Tab 与关键字（材料领用等外链跳转至本页时使用）
     */
    bootstrapFromRoute () {
      const q = this.$route.query || {};
      const kw = q.keyword != null ? String(q.keyword).trim() : '';
      if (q.listTab === 'all' || q.listTab === 'unfinished' || q.listTab === 'finished') {
        this.activeTab = q.listTab;
      } else if (kw) {
        this.activeTab = 'all';
      }
      this.queryForm.status = '';
      if (this.activeTab === 'finished') {
        this.queryForm.status = 'fixed';
      }
      if (kw) {
        this.queryForm.keyword = kw;
      }
    }
  },
  created () {
    this.bootstrapFromRoute();
    this.loadBaseData();
    this.updateStatistics();
    this.loadData();
  },
  activated () {
    if (!this._faultListActivatedOnce) {
      this._faultListActivatedOnce = true;
      return;
    }
    const q = this.$route.query || {};
    if (q.keyword != null || q.listTab != null) {
      this.bootstrapFromRoute();
      this.queryForm.pageNo = 1;
      this.loadData();
      this.updateStatistics();
    }
  }
};
</script>

<style lang="scss" scoped>
.fault-page {
  padding: 20px;
  background: #f5f7fa;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .stats-row {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 15px;
    margin-bottom: 10px;

    .stat-item {
      background: #fff;
      border-radius: 8px;
      padding: 18px;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

      .stat-item-value {
        font-size: 28px;
        font-weight: 600;
        margin-bottom: 5px;
      }

      .stat-item-label {
        font-size: 14px;
        color: #909399;
      }

      &.danger .stat-item-value  { color: #f56c6c; }
      &.warning .stat-item-value { color: #e6a23c; }
      &.success .stat-item-value { color: #67c23a; }
    }
  }

  .card {
    flex: 1;
    min-height: 0;
    background: #fff;
    border-radius: 8px;
    padding: 10px 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    .tabs-header-wrapper {
      position: relative;

      /* 调整 element-ui 默认 tabs 的样式，使其更贴合卡片顶部 */
      ::v-deep .el-tabs__header {
        margin-bottom: 10px;
      }

      ::v-deep .el-tabs__nav-wrap::after {
        /* 如果你想让 tab 下面的灰线贯穿整个屏幕，保留默认；如果觉得线太多，可以设为 height: 0 隐藏 */
        height: 1px;
        background-color: #ebeef5;
      }

      ::v-deep .el-tabs__item {
        font-size: 15px; /* 让 Tab 字号稍微大一点，起到标题的作用 */
        font-weight: 500;
        height: 45px;
        line-height: 45px;
      }

      /* 绝对定位把按钮组定到右侧，和 Tab 文字垂直居中对齐 */
      .action-btn-group {
        position: absolute;
        right: 0;
        top: 8px; /* 根据实际按钮高度和 tab 高度微调，通常 8px-10px 居中 */
        display: flex;
        gap: 10px;
        z-index: 1; /* 确保在 tabs 的下划线之上 */
      }
    }

    .search-bar {
      flex-shrink: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
      padding: 10px;
      background: #f5f7fa;
      border-radius: 4px;

      .filter-switch {
        margin-left: 10px;
      }
    }

    /* 表格填充剩余高度，内容超出时表格内部滚动 */
    .table-wrapper {
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }
  }

  .pagination {
    flex-shrink: 0;
    margin-top: 20px;
    text-align: right;
  }
}

.device-info {
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.device-area {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

/* 缩略图在居中的单元格内不铺满整列，保持单张方块的视觉效果 */
.fault-image-cell {
  justify-content: center;
}

/* 小组委派人辅助文字 */
.assign-delegate-text {
  font-size: 12px;
  color: #409eff;
  margin-top: 3px;
}

.text-muted {
  color: #c0c4cc;
}

.task-info-bar {
  background: #f5f7fa;
  border-radius: 4px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #606266;

  .label { color: #909399; }
  .value { font-weight: 500; }
}

.table-code-link {
  color: #2296f7;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
</style>
